import React, {useMemo, useCallback} from 'react';
import styles from './FundsTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, Icon, User} from '@gravity-ui/uikit';
import {DataTable, formatDateToLocalDate, getUncachedImageUrl, useAvatarCache} from '../../shared';
import {FundModel} from '../../entities';
import {CaretUp, CaretDown} from '@gravity-ui/icons';
import {useNavigate} from 'react-router';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';

type SortProperty = 'name';
type SortOrder = 'ascending' | 'descending';

interface FundsTableProps {
  funds: FundModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  sortProperty?: SortProperty;
  sortOrder?: SortOrder;
  onSortChange?: (property: SortProperty, order: SortOrder) => void;
}

export const FundsTable = ({
  funds,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sortProperty = 'name',
  sortOrder = 'ascending',
  onSortChange,
}: FundsTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();

  // Получаем глобальную версию кеша аватаров
  const {avatarCacheVersion} = useAvatarCache();

  const handleSort = useCallback(
    (property: SortProperty) => {
      if (onSortChange) {
        if (property === sortProperty) {
          const newOrder = sortOrder === 'ascending' ? 'descending' : 'ascending';
          onSortChange(property, newOrder);
        } else {
          // Если выбран новый столбец, устанавливаем сортировку по возрастанию
          onSortChange(property, 'ascending');
        }
      }
    },
    [sortProperty, sortOrder, onSortChange],
  );

  const renderSortIcon = useCallback(
    (property: SortProperty) => {
      if (property !== sortProperty) {
        return <Icon data={CaretDown} className={styles.sortIconInactive} />;
      }

      return sortOrder === 'ascending' ? (
        <Icon data={CaretUp} className={styles.sortIconActive} />
      ) : (
        <Icon data={CaretDown} className={styles.sortIconActive} />
      );
    },
    [sortProperty, sortOrder],
  );

  const handleRowClick = useCallback(
    (fund: FundModel) => {
      navigate(`/fundraisings/funds/${fund.id}`);
    },
    [navigate],
  );

  const columns = useMemo<TableColumnConfig<FundModel>[]>(
    () => [
      {
        id: 'name',
        name: () => (
          <div className={styles.sortableHeader} onClick={() => handleSort('name')}>
            <Text variant="body-1">Название фонда</Text>
            {renderSortIcon('name')}
          </div>
        ),
        width: '50%',
        meta: {
          sortable: true,
        },
        template: (item: FundModel) => (
          <div className={styles.userCellContainer}>
            <User
              size="s"
              avatar={{
                // Используем глобальную версию кеша аватаров
                imgUrl: item.avatarUrl ? getUncachedImageUrl(item.avatarUrl, avatarCacheVersion) : undefined,
                text: item.name,
              }}
              name={item.name}
            />
          </div>
        ),
      },
      {
        id: 'usageCount',
        name: 'Всего сборов',
        width: '25%',
        template: (item: FundModel) => (
          <div className={styles.userCellContainer}>
            <Text variant="body-1">{item.usageCount}</Text>
          </div>
        ),
      },
      {
        id: 'createdAt',
        name: 'Дата создания',
        width: '25%',
        template: (item: FundModel) => (
          <div className={styles.userCellContainer}>
            <Text variant="body-1">{formatDateToLocalDate(item.createdAt)}</Text>
          </div>
        ),
      },
    ],
    [handleSort, renderSortIcon, avatarCacheVersion],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<FundModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={funds || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Фонды не найдены"
        emptyStateDescription="Фонды с таким названием не найдены. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
